import * as React from 'react';
import { groupBy } from 'lodash';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { Page, styles } from '@mq/gatsby-components';

const { colors } = styles.variables;

import IndexLayout from '../layouts';

import { Product } from '@mq/generated-types/dist/Product';
import {
  ProductCategoryPage,
  // ProductCategoryPage_craft_products_Craft_products_products_Entry as ProductsEntry,
  ProductCategoryPage_craft_entry_Craft_productCategoryPages_productCategoryPages_Entry as ProductCategoryPagesEntry,
} from '@mq/generated-types/dist/ProductCategoryPage';
import SubNav from '../components/SubNav';

import ComponentBlocks from '../containers/ComponentBlocks';
import { isQA, getMostRecentDraft } from '../utils/misc';

export const ProductCategoriesFragment = graphql`
  fragment ProductCategories on Craft_products_Category {
    id
    slug
    title
  }
`;

export const ProductCategoryPagesFragment = graphql`
  fragment ProductCategoryPages on Craft_productCategoryPages_productCategoryPages_Entry {
    title
    sourceId
    dateUpdated
    productCategory {
      id
      slug
    }
    pageBlocks {
      ...PageBlocks
    }
  }
`;

export const ProductEntryFragment = graphql`
  fragment Product on Craft_products_products_Entry {
    id
    title
    slug
    sourceId
    dateUpdated
    groupOrder
    productCategory {
      id
      slug
      title
    }
  }
`;

const StyledPage = styled(Page)`
  background-color: ${colors.white};
`;

interface CraftData {
  data: ProductCategoryPage;
}

const ProductCategory: React.FC<CraftData> = ({ data }) => {
  const { entry, drafts, products, draftProducts } = data.craft;
  let typedEntry = entry as ProductCategoryPagesEntry;
  const typedDrafts = drafts as ProductCategoryPagesEntry[];

  // const typedProducts = products as ProductsEntry[];
  const typedProducts = products as Product[];
  // const typedDraftProducts = draftProducts as ProductsEntry[];
  const typedDraftProducts = draftProducts as Product[];

  if (isQA && typedDrafts.length) {
    typedEntry = getMostRecentDraft(typedDrafts);
  }

  // group drafts by sourceId
  const grouped = groupBy(typedDraftProducts, 'sourceId');

  // set this as the default
  // let mergedProducts = products as ProductsEntry[];
  let mergedProducts = products as Product[];

  // we have drafts and are in QA
  if (isQA && typedDraftProducts.length) {
    // peek the sourceId and use to get most recent draft if it exists
    mergedProducts = typedProducts?.map((p) =>
      p.sourceId && grouped[p.sourceId]
        ? getMostRecentDraft(grouped[p.sourceId])
        : p,
    );
  }

  // fix groupOrder
  mergedProducts.sort((a, b) => a.groupOrder - b.groupOrder);

  return typedEntry && typedProducts ? (
    <IndexLayout>
      <StyledPage>
        <>
          <SubNav
            selectedProductId="~"
            products={mergedProducts}
            title={typedEntry.title || ''}
            categorySlug={typedEntry?.productCategory[0]?.slug || ''}
          />
          <ComponentBlocks pageBlocks={typedEntry.pageBlocks} />
        </>
      </StyledPage>
    </IndexLayout>
  ) : null;
};

export default ProductCategory;

export const query = graphql`
  query ProductCategoryPage(
    $slug: [String]!
    $categoryId: [Craft_QueryArgument]!
  ) {
    craft {
      entry(slug: $slug, type: "productCategoryPages") {
        ...ProductCategoryPages
      }
      drafts: entries(slug: $slug, type: "productCategoryPages", drafts: true) {
        ...ProductCategoryPages
      }
      products: entries(
        type: "products"
        orderBy: "groupOrder"
        productCategory: $categoryId
      ) {
        ...Product
      }
      draftProducts: entries(
        type: "products"
        orderBy: "groupOrder"
        productCategory: $categoryId
        drafts: true
      ) {
        ...Product
      }
    }
  }
`;
